import React, { useMemo, useState } from 'react';
import { Search, Bell, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import Portal from '@/components/ui/portal';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import debounce from 'lodash/debounce';
import { cn } from '@/lib/utils';
import { useNotifications } from '@/contexts/NotificationsContext';
import { NotificationBadge } from '@/components/notifications/NotificationBadge';
import { useMediaQuery } from '@/hooks/use-media-query';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

interface TopBarProps {
  onTagSelect?: (tag: string) => void;
}

const TopBar: React.FC<TopBarProps> = ({ onTagSelect }) => {
  const { profile } = useAuth();
  const { showNotifications, setShowNotifications } = useNotifications();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [userResults, setUserResults] = useState<any[]>([]);
  const [tagResults, setTagResults] = useState<string[]>([]);

  const searchUsers = async (query: string) => {
    const q = query.trim();
    if (!q) {
      setUserResults([]);
      return;
    }
    const { data, error } = await supabase
      .from('profiles')
      .select('id, username, display_name, profile_image_url')
      .or(`username.ilike.${q}%,username.ilike.%${q}%,display_name.ilike.%${q}%`)
      .limit(8);
    if (!error) setUserResults(data || []);
  };

  const searchTags = async (query: string) => {
    const q = query.trim().toLowerCase();
    if (!q) {
      setTagResults([]);
      return;
    }
    const { data, error } = await supabase
      .from('bulletins')
      .select('content_labels')
      .not('content_labels', 'is', null)
      .order('created_at', { ascending: false })
      .limit(200);
    if (!error) {
      const all = (data || []).flatMap((r: any) => r.content_labels || []);
      const unique = Array.from(new Set(all.map((t: string) => t.toLowerCase()))).filter(t => t.includes(q)).slice(0, 8);
      setTagResults(unique);
    }
  };

  const debouncedSearch = useMemo(() => debounce((q: string) => {
    setIsSearching(true);
    Promise.all([searchUsers(q), searchTags(q)])
      .finally(() => setIsSearching(false));
  }, 300), []);

  return (
    <header className={cn(
      "sticky top-0 w-full z-30 border-b bg-[#1A1A1B] border-white/10 shadow-[0_4px_20px_-10px_rgba(0,0,0,0.6)]"
    )}>
      <div className="w-full max-w-[1800px] mx-auto px-1 sm:px-2 md:px-4">
        <div className="grid grid-cols-[1fr_max-content] items-center h-16">
          <div className="flex items-center min-w-0">
            {!isDesktop && (
              <button 
                onClick={() => navigate('/loops')}
                className="flex items-center hover:opacity-90 transition-opacity"
                aria-label="Go to home"
              >
                <img
                  src="/fomo_2025.png"
                  alt="FOMO Logo"
                  className="h-12 w-auto"
                />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="ghost" size="icon" aria-label="Search" onClick={() => setSearchOpen(true)}>
              <Search className="w-4 h-4" />
            </Button>
            <div className="relative">
              <Button 
                variant="outline" 
                size="icon" 
                aria-label="Notifications" 
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <Bell className="w-4 h-4" />
              </Button>
              <NotificationBadge />
            </div>
            <Button
              variant="outline"
              size="icon"
              aria-label="Open menu"
              onClick={() => window.dispatchEvent(new CustomEvent('nav:open-menu'))}
            >
              <Menu className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <CommandDialog open={searchOpen} onOpenChange={setSearchOpen}>
            <CommandInput
              placeholder="Search users or tags..."
              value={searchQuery}
              onValueChange={(v) => {
                setSearchQuery(v);
                debouncedSearch(v);
              }}
            />
            <CommandList>
              <CommandEmpty>{isSearching ? 'Searching…' : 'No results'}</CommandEmpty>
              <CommandGroup heading="Users">
                {userResults.map((u) => (
                  <CommandItem key={u.id} onSelect={() => { setSearchOpen(false); navigate(`/user/${u.username}`); }}>
                    <span>{u.display_name || u.username}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandGroup heading="Tags">
                {tagResults.map((t) => (
                  <CommandItem key={t} onSelect={() => { setSearchOpen(false); onTagSelect && onTagSelect(t); }}>
                    <span>#{t}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </CommandDialog>
      </div>
      {/* Menu Drawer Logic Here - simplified for brevity */}
    </header>
  );
};

export default TopBar;
